import React, { useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Save, Edit2, X, Copy, Check, FileText, Mail, Share2, HelpCircle, Calendar, User, Send } from 'lucide-react';
import EmailPreview from './EmailPreview';

interface TextPreviewProps {
  asset: {
    id: number;
    file_name: string;
    asset_type: string;
    content?: string;
    metadata?: any;
  };
  apiBaseUrl: string;
  onSave?: (assetId: number, content: string) => Promise<void>;
}

export default function TextPreview({ asset, apiBaseUrl, onSave }: TextPreviewProps) {
  // Parse metadata
  const parseMetadata = () => {
    if (asset.metadata) {
      if (typeof asset.metadata === 'string') {
        try {
          return JSON.parse(asset.metadata);
        } catch (e) {
          return {};
        }
      }
      return asset.metadata;
    }
    return {};
  };

  const initialMetadata = parseMetadata();

  const [isEditing, setIsEditing] = useState(false);
  const [isEditingMeta, setIsEditingMeta] = useState(false);
  const [editedContent, setEditedContent] = useState(asset.content || '');
  const [isSaving, setIsSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  // Email-specific metadata fields
  const [emailMetadata, setEmailMetadata] = useState({
    subject: initialMetadata.subject || '',
    preheader: initialMetadata.preheader || '',
    from_name: initialMetadata.from_name || '',
    from_email: initialMetadata.from_email || '',
    reply_to: initialMetadata.reply_to || '',
    send_date: initialMetadata.send_date || '',
    send_time: initialMetadata.send_time || '',
    campaign_name: initialMetadata.campaign_name || '',
    segment: initialMetadata.segment || '',
    tags: Array.isArray(initialMetadata.tags) ? initialMetadata.tags.join(', ') : initialMetadata.tags || ''
  });

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      ['link', 'image'],
      ['clean']
    ]
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'blockquote', 'code-block', 'list', 'bullet',
    'link', 'image', 'color', 'background'
  ];

  const handleSave = async () => {
    if (!onSave) return;
    setIsSaving(true);
    try {
      // Save just the content, not wrapped in JSON
      // The metadata should be handled separately through a different API if needed
      await onSave(asset.id, editedContent);
      setIsEditing(false);
      setIsEditingMeta(false);
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopy = () => {
    const textContent = editedContent.replace(/<[^>]*>/g, ''); // Strip HTML
    navigator.clipboard.writeText(textContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCancel = () => {
    setEditedContent(asset.content || '');
    setIsEditing(false);
    setIsEditingMeta(false);
  };

  const getAssetIcon = () => {
    switch (asset.asset_type) {
      case 'email':
        return <Mail className="w-5 h-5 text-blue-600" />;
      case 'social':
        return <Share2 className="w-5 h-5 text-purple-600" />;
      case 'faq':
        return <HelpCircle className="w-5 h-5 text-green-600" />;
      default:
        return <FileText className="w-5 h-5 text-gray-600" />;
    }
  };

  const getPreviewTitle = () => {
    switch (asset.asset_type) {
      case 'email':
        return 'Email Campaign Editor';
      case 'social':
        return 'Social Media Post';
      case 'faq':
        return 'FAQ Content';
      case 'page':
        return 'Page Content';
      case 'copy':
        return 'Marketing Copy';
      default:
        return 'Text Content';
    }
  };

  // Render email preview with editable metadata
  if (asset.asset_type === 'email') {
    return <EmailPreview asset={asset} apiBaseUrl={apiBaseUrl} onSave={onSave} />;
  }

  // Old email code removed - now using EmailPreview component
  if (false) { // Temporarily disabled old email code
    return (
      <div className="h-full flex flex-col bg-gray-50">
        <div className="bg-white border-b px-4 py-2 pr-64 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {getAssetIcon()}
            <span className="font-medium">{getPreviewTitle()}</span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleCopy}
              className="flex items-center space-x-1 px-3 py-1.5 hover:bg-gray-100 rounded"
            >
              {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              <span className="text-sm">{copied ? 'Copied!' : 'Copy'}</span>
            </button>
            {!isEditingMeta ? (
              <button
                onClick={() => setIsEditingMeta(true)}
                className="flex items-center space-x-2 px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                <Edit2 className="w-4 h-4" />
                <span className="text-sm">Edit Email</span>
              </button>
            ) : (
              <>
                <button
                  onClick={handleCancel}
                  className="flex items-center space-x-1 px-3 py-1.5 hover:bg-gray-100 rounded"
                >
                  <X className="w-4 h-4" />
                  <span className="text-sm">Cancel</span>
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex items-center space-x-2 px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400"
                >
                  <Save className="w-4 h-4" />
                  <span className="text-sm">{isSaving ? 'Saving...' : 'Save'}</span>
                </button>
              </>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          <div className="max-w-5xl mx-auto p-6">
            <div className="flex gap-6">
              {/* Email Metadata Panel */}
              <div className="w-96">
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <Mail className="w-5 h-5 mr-2 text-blue-600" />
                    Email Settings
                  </h3>

                  <div className="space-y-4">
                    {/* Subject Line */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Subject Line *
                      </label>
                      {isEditingMeta ? (
                        <input
                          type="text"
                          value={emailMetadata.subject}
                          onChange={(e) => setEmailMetadata({ ...emailMetadata, subject: e.target.value })}
                          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Your email subject..."
                        />
                      ) : (
                        <p className="px-3 py-2 bg-gray-50 rounded text-gray-900">
                          {emailMetadata.subject || 'No subject'}
                        </p>
                      )}
                      {isEditingMeta && (
                        <p className="text-xs text-gray-500 mt-1">
                          Keep it under 50 characters for best results
                        </p>
                      )}
                    </div>

                    {/* Preheader */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Preheader Text
                      </label>
                      {isEditingMeta ? (
                        <textarea
                          value={emailMetadata.preheader}
                          onChange={(e) => setEmailMetadata({ ...emailMetadata, preheader: e.target.value })}
                          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-16"
                          placeholder="Preview text shown in inbox..."
                        />
                      ) : (
                        <p className="px-3 py-2 bg-gray-50 rounded text-gray-900">
                          {emailMetadata.preheader || 'No preheader'}
                        </p>
                      )}
                      {isEditingMeta && (
                        <p className="text-xs text-gray-500 mt-1">
                          85-100 characters recommended
                        </p>
                      )}
                    </div>

                    {/* From Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        From Name *
                      </label>
                      {isEditingMeta ? (
                        <input
                          type="text"
                          value={emailMetadata.from_name}
                          onChange={(e) => setEmailMetadata({ ...emailMetadata, from_name: e.target.value })}
                          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Sender name..."
                        />
                      ) : (
                        <p className="px-3 py-2 bg-gray-50 rounded text-gray-900">
                          {emailMetadata.from_name || 'Not specified'}
                        </p>
                      )}
                    </div>

                    {/* From Email */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        From Email *
                      </label>
                      {isEditingMeta ? (
                        <input
                          type="email"
                          value={emailMetadata.from_email}
                          onChange={(e) => setEmailMetadata({ ...emailMetadata, from_email: e.target.value })}
                          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="sender@example.com"
                        />
                      ) : (
                        <p className="px-3 py-2 bg-gray-50 rounded text-gray-900">
                          {emailMetadata.from_email || 'Not specified'}
                        </p>
                      )}
                    </div>

                    {/* Reply To */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Reply-To Email
                      </label>
                      {isEditingMeta ? (
                        <input
                          type="email"
                          value={emailMetadata.reply_to}
                          onChange={(e) => setEmailMetadata({ ...emailMetadata, reply_to: e.target.value })}
                          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="reply@example.com"
                        />
                      ) : (
                        <p className="px-3 py-2 bg-gray-50 rounded text-gray-900">
                          {emailMetadata.reply_to || emailMetadata.from_email || 'Same as From'}
                        </p>
                      )}
                    </div>

                    {/* Send Date & Time */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Send Date
                        </label>
                        {isEditingMeta ? (
                          <input
                            type="date"
                            value={emailMetadata.send_date}
                            onChange={(e) => setEmailMetadata({ ...emailMetadata, send_date: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        ) : (
                          <p className="px-3 py-2 bg-gray-50 rounded text-gray-900 text-sm">
                            {emailMetadata.send_date || 'Not set'}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Send Time
                        </label>
                        {isEditingMeta ? (
                          <input
                            type="time"
                            value={emailMetadata.send_time}
                            onChange={(e) => setEmailMetadata({ ...emailMetadata, send_time: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        ) : (
                          <p className="px-3 py-2 bg-gray-50 rounded text-gray-900 text-sm">
                            {emailMetadata.send_time || 'Not set'}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Campaign Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Campaign Name
                      </label>
                      {isEditingMeta ? (
                        <input
                          type="text"
                          value={emailMetadata.campaign_name}
                          onChange={(e) => setEmailMetadata({ ...emailMetadata, campaign_name: e.target.value })}
                          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Q4 Newsletter"
                        />
                      ) : (
                        <p className="px-3 py-2 bg-gray-50 rounded text-gray-900">
                          {emailMetadata.campaign_name || 'Not specified'}
                        </p>
                      )}
                    </div>

                    {/* Segment */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Audience Segment
                      </label>
                      {isEditingMeta ? (
                        <input
                          type="text"
                          value={emailMetadata.segment}
                          onChange={(e) => setEmailMetadata({ ...emailMetadata, segment: e.target.value })}
                          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="All Subscribers"
                        />
                      ) : (
                        <p className="px-3 py-2 bg-gray-50 rounded text-gray-900">
                          {emailMetadata.segment || 'All Subscribers'}
                        </p>
                      )}
                    </div>

                    {/* Tags */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tags (comma-separated)
                      </label>
                      {isEditingMeta ? (
                        <input
                          type="text"
                          value={emailMetadata.tags}
                          onChange={(e) => setEmailMetadata({ ...emailMetadata, tags: e.target.value })}
                          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="newsletter, promotional, q4"
                        />
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {emailMetadata.tags ? (
                            emailMetadata.tags.split(',').map((tag, i) => (
                              <span key={i} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                                {tag.trim()}
                              </span>
                            ))
                          ) : (
                            <span className="text-gray-500 text-sm">No tags</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Email Preview */}
              <div className="flex-1">
                <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                  {/* Email Header Mockup */}
                  <div className="bg-gray-100 px-4 py-3 border-b">
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center">
                        <span className="font-medium text-gray-600 w-20">From:</span>
                        <span className="text-gray-900">
                          {emailMetadata.from_name || 'Sender'} &lt;{emailMetadata.from_email || 'email@example.com'}&gt;
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium text-gray-600 w-20">Subject:</span>
                        <span className="text-gray-900 font-medium">
                          {emailMetadata.subject || 'Email Subject Line'}
                        </span>
                      </div>
                      {emailMetadata.preheader && (
                        <div className="flex items-center">
                          <span className="font-medium text-gray-600 w-20">Preview:</span>
                          <span className="text-gray-600 text-xs">
                            {emailMetadata.preheader}
                          </span>
                        </div>
                      )}
                      {emailMetadata.send_date && (
                        <div className="flex items-center">
                          <span className="font-medium text-gray-600 w-20">
                            <Calendar className="w-4 h-4 inline mr-1" />
                            Schedule:
                          </span>
                          <span className="text-gray-600">
                            {emailMetadata.send_date} {emailMetadata.send_time && `at ${emailMetadata.send_time}`}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Email Body */}
                  <div className="p-6">
                    {isEditingMeta ? (
                      <ReactQuill
                        value={editedContent}
                        onChange={setEditedContent}
                        modules={quillModules}
                        formats={quillFormats}
                        className="min-h-[400px]"
                        placeholder="Compose your email content here..."
                      />
                    ) : (
                      <div
                        className="prose max-w-none"
                        dangerouslySetInnerHTML={{ __html: editedContent || '<p class="text-gray-500">No content yet. Click Edit to add email content.</p>' }}
                      />
                    )}
                  </div>

                  {/* Email Footer */}
                  <div className="bg-gray-50 px-6 py-4 border-t text-xs text-gray-600">
                    <p className="text-center">
                      This email was sent to [recipient@example.com]<br />
                      <a href="#" className="text-blue-600 hover:underline">Unsubscribe</a> |
                      <a href="#" className="text-blue-600 hover:underline ml-1">View in browser</a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render social media preview
  if (asset.asset_type === 'social') {
    // Parse social media content
    const parseSocialContent = () => {
      try {
        return typeof editedContent === 'string' ? JSON.parse(editedContent) : editedContent;
      } catch (e) {
        return { metadata: {} };
      }
    };

    const socialData = parseSocialContent();
    const platformData = socialData.metadata || socialData;
    const [selectedPlatform, setSelectedPlatform] = useState('linkedin');

    // Helper functions to extract platform content
    const getLinkedInContent = () => {
      // First try to get content from linkedin object
      if (platformData.linkedin && (platformData.linkedin.text || platformData.linkedin.headline)) {
        return platformData.linkedin;
      }

      // Fallback to platforms array
      if (platformData.platforms && Array.isArray(platformData.platforms)) {
        const linkedinPlatform = platformData.platforms.find((p: any) => p.platform === 'linkedin');
        if (linkedinPlatform) {
          return {
            text: linkedinPlatform.content || linkedinPlatform.text || '',
            headline: linkedinPlatform.headline || '',
            link: linkedinPlatform.link || '',
            visibility: 'Public',
            postType: linkedinPlatform.post_type || 'Text'
          };
        }
      }

      // Final fallback to general campaign content
      if (platformData.campaign || platformData.content) {
        return {
          text: platformData.content || '',
          headline: platformData.campaign || '',
          visibility: 'Public',
          postType: 'Text'
        };
      }

      return { text: '', headline: '', visibility: 'Public', postType: 'Text' };
    };

    const getGoogleBusinessContent = () => {
      // First try to get content from googlebusiness object
      if (platformData.googlebusiness && platformData.googlebusiness.text) {
        return platformData.googlebusiness;
      }

      // Fallback to platforms array
      if (platformData.platforms && Array.isArray(platformData.platforms)) {
        const googlePlatform = platformData.platforms.find((p: any) => p.platform === 'googlebusiness' || p.platform === 'google_business');
        if (googlePlatform) {
          return {
            text: googlePlatform.content || googlePlatform.text || '',
            postType: googlePlatform.post_type || 'Update',
            callToAction: googlePlatform.cta_button || googlePlatform.callToAction || '',
            eventTitle: googlePlatform.event_title || '',
            eventDate: googlePlatform.event_date || '',
            eventTime: googlePlatform.event_time || '',
            offerTitle: googlePlatform.offer_title || '',
            offerTerms: googlePlatform.offer_terms || ''
          };
        }
      }

      // Final fallback to general campaign content
      if (platformData.campaign || platformData.content) {
        return {
          text: platformData.content || '',
          postType: 'Update',
          callToAction: ''
        };
      }

      return { text: '', postType: 'Update', callToAction: '' };
    };

    // Platform preview components
    const LinkedInPreview = ({ data }: { data: any }) => (
      <div className="bg-white border rounded-lg p-4 max-w-md mx-auto">
        <div className="flex items-start space-x-3 mb-3">
          <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
            <span className="text-white font-bold text-sm">in</span>
          </div>
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              <p className="font-semibold text-sm">Company Name</p>
              <span className="text-xs text-gray-500">• 1st</span>
            </div>
            <p className="text-xs text-gray-500">2h • LinkedIn</p>
          </div>
        </div>

        {data.headline && (
          <h3 className="font-semibold text-sm mb-2">{data.headline}</h3>
        )}

        <div className="text-sm text-gray-900 mb-3">
          {data.text || 'Post text will appear here...'}
        </div>

        {data.link && (
          <div className="border rounded p-3 mb-3">
            <div className="w-full h-32 bg-gray-200 rounded mb-2"></div>
            <p className="text-xs text-gray-500">example.com</p>
            <p className="font-medium text-sm">{data.link}</p>
          </div>
        )}

        <div className="flex items-center justify-between pt-2 border-t">
          <span className="text-xs text-gray-500">👍 ❤️ 👏 {Math.floor(Math.random() * 50) + 10}</span>
          <span className="text-xs text-gray-500">{Math.floor(Math.random() * 20) + 1} comments</span>
        </div>

        <div className="flex items-center justify-around pt-3 border-t mt-2">
          <button className="flex items-center space-x-1 text-gray-600 hover:bg-gray-50 px-3 py-1 rounded">
            <span className="text-sm">👍</span>
            <span className="text-xs">Like</span>
          </button>
          <button className="flex items-center space-x-1 text-gray-600 hover:bg-gray-50 px-3 py-1 rounded">
            <span className="text-sm">💬</span>
            <span className="text-xs">Comment</span>
          </button>
          <button className="flex items-center space-x-1 text-gray-600 hover:bg-gray-50 px-3 py-1 rounded">
            <span className="text-sm">↗️</span>
            <span className="text-xs">Share</span>
          </button>
        </div>

        {/* LinkedIn Settings */}
        <div className="mt-4 p-3 bg-gray-50 rounded text-xs">
          <p><strong>Visibility:</strong> {data.visibility || 'Public'}</p>
          {data.targeting && <p><strong>Targeting:</strong> {data.targeting}</p>}
          {data.scheduledDate && (
            <p><strong>Scheduled:</strong> {data.scheduledDate} {data.scheduledTime}</p>
          )}
        </div>
      </div>
    );

    const GoogleBusinessPreview = ({ data }: { data: any }) => (
      <div className="bg-white border rounded-lg p-4 max-w-md mx-auto">
        <div className="flex items-start space-x-3 mb-3">
          <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
            <span className="text-white font-bold text-xs">GB</span>
          </div>
          <div className="flex-1">
            <p className="font-semibold text-sm">Your Business Name</p>
            <p className="text-xs text-gray-500">Business • Local business</p>
          </div>
        </div>

        <div className="text-sm text-gray-900 mb-3">
          {data.text || 'Google Business post content will appear here...'}
        </div>

        {data.postType === 'Offer' && data.offerTitle && (
          <div className="bg-red-50 border border-red-200 rounded p-3 mb-3">
            <div className="flex items-center space-x-2 mb-1">
              <span className="bg-red-500 text-white text-xs px-2 py-1 rounded">OFFER</span>
            </div>
            <p className="font-semibold text-sm">{data.offerTitle}</p>
            {data.offerTerms && <p className="text-xs text-gray-600 mt-1">{data.offerTerms}</p>}
          </div>
        )}

        {data.postType === 'Event' && data.eventTitle && (
          <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-3">
            <div className="flex items-center space-x-2 mb-1">
              <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded">EVENT</span>
            </div>
            <p className="font-semibold text-sm">{data.eventTitle}</p>
            {data.eventDate && (
              <p className="text-xs text-gray-600 mt-1">
                📅 {data.eventDate} {data.eventTime}
              </p>
            )}
          </div>
        )}

        {data.callToAction && (
          <button className="bg-blue-600 text-white text-sm px-4 py-2 rounded hover:bg-blue-700 mb-3">
            {data.callToAction}
          </button>
        )}

        <div className="flex items-center justify-between pt-2 border-t">
          <span className="text-xs text-gray-500">👍 {Math.floor(Math.random() * 30) + 5}</span>
          <span className="text-xs text-gray-500">{Math.floor(Math.random() * 10) + 1} views</span>
        </div>

        {/* Google Business Settings */}
        <div className="mt-4 p-3 bg-gray-50 rounded text-xs">
          <p><strong>Post Type:</strong> {data.postType || 'Update'}</p>
          {data.scheduledDate && (
            <p><strong>Scheduled:</strong> {data.scheduledDate} {data.scheduledTime}</p>
          )}
        </div>
      </div>
    );

    return (
      <div className="h-full flex flex-col bg-gray-50">
        <div className="bg-white border-b px-4 py-2 pr-64 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {getAssetIcon()}
            <span className="font-medium">{getPreviewTitle()}</span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleCopy}
              className="flex items-center space-x-1 px-3 py-1.5 hover:bg-gray-100 rounded"
            >
              {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              <span className="text-sm">{copied ? 'Copied!' : 'Copy'}</span>
            </button>
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center space-x-2 px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                <Edit2 className="w-4 h-4" />
                <span className="text-sm">Edit</span>
              </button>
            ) : (
              <>
                <button
                  onClick={handleCancel}
                  className="flex items-center space-x-1 px-3 py-1.5 hover:bg-gray-100 rounded"
                >
                  <X className="w-4 h-4" />
                  <span className="text-sm">Cancel</span>
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex items-center space-x-2 px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400"
                >
                  <Save className="w-4 h-4" />
                  <span className="text-sm">{isSaving ? 'Saving...' : 'Save'}</span>
                </button>
              </>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {isEditing ? (
            /* Editing Mode - JSON Editor */
            <div className="h-full p-6">
              <div className="max-w-4xl mx-auto h-full">
                <h3 className="text-lg font-semibold mb-4">Edit Social Media Content</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Edit the raw JSON content below. This will update all platform previews.
                </p>
                <div className="h-full">
                  <textarea
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    className="w-full h-5/6 p-4 border border-gray-300 rounded-lg font-mono text-sm resize-none"
                    placeholder="Enter JSON content here..."
                  />
                </div>
              </div>
            </div>
          ) : (
            /* Preview Mode - Platform Previews */
            <div className="flex h-full">
              {/* Platform Selector */}
              <div className="w-64 bg-white border-r p-4">
                <h3 className="font-semibold mb-4">Platform Previews</h3>
                <div className="space-y-2">
                  {['linkedin', 'googlebusiness', 'facebook', 'instagram', 'twitter'].map((platform) => (
                    <button
                      key={platform}
                      onClick={() => setSelectedPlatform(platform)}
                      className={`w-full text-left px-3 py-2 rounded text-sm ${
                        selectedPlatform === platform
                          ? 'bg-blue-100 text-blue-700 font-medium'
                          : 'hover:bg-gray-100'
                      }`}
                    >
                      {platform === 'linkedin' && '💼 LinkedIn'}
                      {platform === 'googlebusiness' && '🏢 Google Business'}
                      {platform === 'facebook' && '📘 Facebook'}
                      {platform === 'instagram' && '📷 Instagram'}
                      {platform === 'twitter' && '🐦 Twitter'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Platform Preview */}
              <div className="flex-1 p-6">
                {selectedPlatform === 'linkedin' && (
                  <LinkedInPreview data={getLinkedInContent()} />
                )}
                {selectedPlatform === 'googlebusiness' && (
                  <GoogleBusinessPreview data={getGoogleBusinessContent()} />
                )}
                {(selectedPlatform === 'facebook' || selectedPlatform === 'instagram' || selectedPlatform === 'twitter') && (
                  <div className="bg-white border rounded-lg p-8 max-w-md mx-auto text-center">
                    <p className="text-gray-500 mb-4">
                      {selectedPlatform === 'facebook' && '📘 Facebook preview'}
                      {selectedPlatform === 'instagram' && '📷 Instagram preview'}
                      {selectedPlatform === 'twitter' && '🐦 Twitter preview'}
                    </p>
                    <p className="text-sm text-gray-400">
                      Platform preview coming soon. Use the raw JSON view to see {selectedPlatform} content.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Default text preview
  return (
    <div className="h-full flex flex-col bg-gray-50">
      <div className="bg-white border-b px-4 py-2 pr-64 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {getAssetIcon()}
          <span className="font-medium">{getPreviewTitle()}</span>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleCopy}
            className="flex items-center space-x-1 px-3 py-1.5 hover:bg-gray-100 rounded"
          >
            {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
            <span className="text-sm">{copied ? 'Copied!' : 'Copy'}</span>
          </button>
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center space-x-2 px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              <Edit2 className="w-4 h-4" />
              <span className="text-sm">Edit</span>
            </button>
          ) : (
            <>
              <button
                onClick={handleCancel}
                className="flex items-center space-x-1 px-3 py-1.5 hover:bg-gray-100 rounded"
              >
                <X className="w-4 h-4" />
                <span className="text-sm">Cancel</span>
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center space-x-2 px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400"
              >
                <Save className="w-4 h-4" />
                <span className="text-sm">{isSaving ? 'Saving...' : 'Save'}</span>
              </button>
            </>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-sm p-8">
          {isEditing ? (
            <ReactQuill
              value={editedContent}
              onChange={setEditedContent}
              modules={quillModules}
              formats={quillFormats}
              className="min-h-[400px]"
            />
          ) : (
            <div
              className="prose max-w-none"
              dangerouslySetInnerHTML={{ __html: editedContent }}
            />
          )}
        </div>
      </div>
    </div>
  );
}